import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ORDER_STATUS_COLORS, ORDER_STATUS_LABELS, OrderStatus } from "@/types/order/order.common";
import { formatDateAndTime } from "@/utils/dateAndTimeFormat";
import { BankOutlined, CheckCircleOutlined, EnvironmentOutlined, ExclamationCircleOutlined, InfoCircleOutlined, NumberOutlined, ShoppingCartOutlined, StopOutlined, ThunderboltFilled, ThunderboltOutlined, } from "@ant-design/icons";
import { Avatar, Button, Card, Descriptions, Divider, Modal, Space, Tag, Typography, } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
const { Text } = Typography;
const isValidTime = (time) => time && dayjs(time).isValid();
// Форматирует минуты в читаемый формат с днями, часами и минутами
const formatMinutesToReadable = (minutes) => {
    if (minutes < 60) {
        return `${minutes} мин`;
    }
    const totalHours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    // Если более 24 часов - показываем дни
    if (totalHours >= 24) {
        const days = Math.floor(totalHours / 24);
        const hours = totalHours % 24;
        if (hours === 0 && remainingMinutes === 0) {
            return `${days} д.`;
        }
        if (remainingMinutes === 0) {
            return `${days} д. ${hours} ч`;
        }
        if (hours === 0) {
            return `${days} д. ${remainingMinutes} мин`;
        }
        return `${days} д. ${hours} ч ${remainingMinutes} мин`;
    }
    // Менее 24 часов - показываем часы и минуты
    if (remainingMinutes === 0) {
        return `${totalHours} ч`;
    }
    return `${totalHours} ч ${remainingMinutes} мин`;
};
const createLabel = (icon, text, color) => (_jsxs(Space, { size: "small", children: [_jsx("span", { style: { color }, children: icon }), _jsx(Text, { children: text })] }));
const createChargeTag = (value) => (_jsxs(Tag, { color: !value ? "red" : value >= 80 ? "green" : value >= 50 ? "orange" : "red", children: [_jsx(ThunderboltOutlined, {}), " ", value ? `${value}%` : "Не указан"] }));
const createStatusTag = (plannedTime, expectedTime) => {
    if (!plannedTime || !expectedTime)
        return null;
    const diff = dayjs(plannedTime).diff(expectedTime, "minutes");
    const isOnTime = diff >= 0;
    const minutes = Math.abs(diff);
    return (_jsxs(Tag, { color: isOnTime ? "green" : "red", children: [isOnTime ? _jsx(CheckCircleOutlined, {}) : _jsx(ExclamationCircleOutlined, {}), isOnTime
                ? ` Запас: ${formatMinutesToReadable(minutes)}`
                : ` Задержка: ${formatMinutesToReadable(minutes)}`] }));
};
export const CommonPoint = ({ point }) => {
    const { plannedArrivalTime, plannedDepartureTime, expectedArrivalTime, expectedDepartureTime, actualArrivalTime, actualDepartureTime, } = point;
    const timeData = [
        {
            key: "planned-arrival",
            time: plannedArrivalTime,
            label: "План прибытие",
            color: "green",
        },
        {
            key: "planned-departure",
            time: plannedDepartureTime,
            label: "План отбытие",
            color: "orange",
        },
        {
            key: "expected-arrival",
            time: expectedArrivalTime,
            label: "Ожид прибытие",
            color: "purple",
        },
        {
            key: "expected-departure",
            time: expectedDepartureTime,
            label: "Ожид отбытие",
            color: "purple",
        },
        {
            key: "actual-arrival",
            time: actualArrivalTime,
            label: "Факт прибытие",
            color: "blue",
        },
        {
            key: "actual-departure",
            time: actualDepartureTime,
            label: "Факт отбытие",
            color: "blue",
        },
    ];
    const timeItems = timeData
        .filter(({ time }) => isValidTime(time))
        .map(({ key, time, label, color }) => ({
        key,
        label: label,
        children: _jsx(Tag, { color: color, children: formatDateAndTime(time) }),
    }));
    const arrivalStatus = createStatusTag(plannedArrivalTime, expectedArrivalTime);
    const departureStatus = createStatusTag(plannedDepartureTime, expectedDepartureTime);
    if (arrivalStatus) {
        timeItems.push({
            key: "arrival-status",
            label: "Стат. прибытия",
            children: arrivalStatus,
        });
    }
    if (departureStatus) {
        timeItems.push({
            key: "departure-status",
            label: "Стат. отбытия",
            children: departureStatus,
        });
    }
    return (_jsxs(Space, { direction: "vertical", size: "small", style: { width: "100%" }, children: [_jsx(Divider, { style: { margin: "6px 0" }, children: _jsx(Text, { type: "secondary", style: { fontSize: "12px" }, children: "\u0412\u0440\u0435\u043C\u0435\u043D\u043D\u0430\u044F \u0441\u0445\u0435\u043C\u0430 \u0432\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u044F" }) }), _jsx(Descriptions, { size: "small", column: { xs: 1, sm: 2, md: 3, lg: 4 }, items: timeItems })] }));
};
export const RoutePointBase = ({ point, base, point: { addressInfo }, }) => {
    const orderItems = [
        {
            key: "address",
            label: createLabel(_jsx(EnvironmentOutlined, {}), "Адрес"),
            children: addressInfo.getAddress() || "Адрес не указан",
        },
    ];
    return (_jsxs(Card, { size: "small", style: { width: "100%" }, children: [_jsxs(Space, { size: "small", style: { paddingTop: "4px" }, children: [_jsx(Avatar, { size: "small", icon: _jsx(BankOutlined, {}), style: { backgroundColor: "#1890ff" } }), _jsx(Text, { strong: true, style: { color: "#1890ff" }, children: base?.name || "Базовая точка" })] }), _jsx(Descriptions, { size: "small", column: { xs: 1, sm: 1, md: 2 }, items: orderItems }), _jsx(CommonPoint, { point: point })] }));
};
/**
 * Возвращает Tag со статусом заказа.
 * Принимает статус либо из OrderDom (при построении маршрута),
 * либо из денормализованного поля point.orderStatus (при просмотре сохранённого маршрута).
 */
const createOrderStatusTag = (orderStatus) => {
    if (!orderStatus)
        return _jsx(Text, { type: "secondary", children: "\u041D\u0435 \u0443\u043A\u0430\u0437\u0430\u043D" });
    const knownStatus = Object.values(OrderStatus).includes(orderStatus)
        ? orderStatus
        : null;
    const label = knownStatus
        ? ORDER_STATUS_LABELS[knownStatus]
        : orderStatus;
    const color = knownStatus ? ORDER_STATUS_COLORS[knownStatus] : "default";
    return _jsx(Tag, { color: color, children: label });
};
export const RoutePointOrder = ({ point, order, point: { addressInfo }, onCancelOrder, }) => {
    const navigate = useNavigate();
    const [confirmOpen, setConfirmOpen] = useState(false);
    // Статус заказа: из OrderDom (при редактировании) или из денормализованного поля (при просмотре)
    const orderStatusValue = order?.status ?? point.orderStatus ?? null;
    // Номер заказа: из OrderDom или из денормализованного поля
    const orderNumberValue = order?.orderNumber ?? point.orderNumber ?? null;
    // ID заказа для ссылки
    const orderId = order?.id ?? point.idOrder ?? undefined;
    // Точка визуально "отменена" если:
    // 1. Поле isDeleted=true (soft-delete в route_points), ИЛИ
    // 2. Статус связанного заказа — CANCELLED или DISPATCHER_CANCELLED
    const isCancelledByStatus = orderStatusValue === OrderStatus.CANCELLED ||
        orderStatusValue === OrderStatus.DISPATCHER_CANCELLED;
    const isDeleted = point.isDeleted === true || isCancelledByStatus;
    const orderItems = [
        {
            key: "address",
            label: createLabel(_jsx(EnvironmentOutlined, {}), "Адрес"),
            children: addressInfo.getAddress() || "Адрес не указан",
        },
        {
            key: "orderNumber",
            label: createLabel(_jsx(NumberOutlined, {}), "Номер заказа"),
            children: (_jsxs(Space, { size: 6, children: [orderId ? (_jsx(Typography.Link, { onClick: () => navigate(`/orders/${orderId}`), style: { fontFamily: "monospace", fontWeight: "bold" }, children: orderNumberValue || "Не указан" })) : (_jsx(Text, { code: true, strong: true, style: { color: "#1890ff" }, children: orderNumberValue || "Не указан" })), onCancelOrder && !isDeleted && (_jsx(Button, { danger: true, type: "text", size: "small", icon: _jsx(StopOutlined, {}), onClick: () => setConfirmOpen(true), style: { padding: "0 4px", height: "auto", lineHeight: "inherit" }, children: "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C" }))] })),
        },
        {
            key: "orderStatus",
            label: createLabel(_jsx(InfoCircleOutlined, {}), "Статус заказа"),
            children: createOrderStatusTag(orderStatusValue),
        },
        {
            key: "chargeLevel",
            label: createLabel(_jsx(ThunderboltFilled, {}), "Требуемый заряд"),
            children: createChargeTag(order?.chargeValue || null),
        },
        {
            key: "currentCharge",
            label: createLabel(_jsx(ThunderboltFilled, {}), "Текущий заряд"),
            children: createChargeTag(order?.currentCharge || null),
        },
    ];
    return (_jsxs(Card, { size: "small", style: {
            width: "100%",
            opacity: isDeleted ? 0.6 : 1,
            borderColor: isDeleted ? "#d9d9d9" : undefined,
            backgroundColor: isDeleted ? "#fafafa" : undefined,
        }, children: [_jsxs(Space, { size: "small", style: { paddingTop: "4px" }, children: [_jsx(Avatar, { size: "small", icon: _jsx(ShoppingCartOutlined, {}), style: { backgroundColor: isDeleted ? "#aaa" : "#52c41a" } }), _jsx(Text, { strong: true, style: { color: isDeleted ? "#aaa" : "#52c41a" }, children: "\u0417\u0430\u043A\u0430\u0437 \u043D\u0430 \u043E\u0431\u0441\u043B\u0443\u0436\u0438\u0432\u0430\u043D\u0438\u0435" }), isDeleted && (_jsxs(Tag, { color: "red", style: { marginLeft: 4 }, children: [_jsx(StopOutlined, {}), " \u041E\u0442\u043C\u0435\u043D\u0451\u043D"] }))] }), _jsx(Descriptions, { size: "small", column: { xs: 1, sm: 1, md: 2 }, items: orderItems }), onCancelOrder && !isDeleted && (_jsx(Modal, { title: "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0437\u0430\u043A\u0430\u0437?", open: confirmOpen, onOk: () => {
                    onCancelOrder();
                    setConfirmOpen(false);
                }, onCancel: () => setConfirmOpen(false), okText: "\u0414\u0430, \u043E\u0442\u043C\u0435\u043D\u0438\u0442\u044C", cancelText: "\u041D\u0435\u0442", okButtonProps: { danger: true }, children: _jsxs("p", { children: ["\u0417\u0430\u043A\u0430\u0437 \u0431\u0443\u0434\u0435\u0442 \u043F\u0435\u0440\u0435\u0432\u0435\u0434\u0451\u043D \u0432 \u0441\u0442\u0430\u0442\u0443\u0441", " ", _jsx("strong", { children: "\u041E\u0442\u043C\u0435\u043D\u0451\u043D \u0434\u0438\u0441\u043F\u0435\u0442\u0447\u0435\u0440\u043E\u043C" }), ". \u0422\u043E\u0447\u043A\u0430 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0430 \u0431\u0443\u0434\u0435\u0442 \u0443\u0434\u0430\u043B\u0435\u043D\u0430 \u0438\u0437 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0430. \u0415\u0441\u043B\u0438 \u0432 \u0434\u0430\u043D\u043D\u044B\u0439 \u043C\u043E\u043C\u0435\u043D\u0442 \u0438\u0434\u0451\u0442 \u0437\u0430\u0440\u044F\u0434\u043A\u0430 \u2014 \u0441\u0435\u0441\u0441\u0438\u044F \u0431\u0443\u0434\u0435\u0442 \u0437\u0430\u0432\u0435\u0440\u0448\u0435\u043D\u0430 \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438."] }) })), _jsx(CommonPoint, { point: point })] }));
};
